import { auth } from "gather-firebase-client/dist/src/public/firebase";
const MINUTES_TO_MILLISECONDS = 60000;
const NETWORK_RETRY_INTERVAL_MILLISECONDS = 5000;
const TOKEN_EXPIRATION_BUFFER_MINUTES = 10;
const TOKEN_EXPIRATION_BUFFER_MILLISECONDS = TOKEN_EXPIRATION_BUFFER_MINUTES * MINUTES_TO_MILLISECONDS;
class AuthManager {
    static instance = new AuthManager();
    _authToken;
    _activeRefreshTokenCall;
    _refreshTokenTimeout;
    constructor() {
        auth.onIdTokenChanged(async (user) => {
            if (!user) {
                this._authToken = undefined;
                if (this._refreshTokenTimeout !== undefined) {
                    clearTimeout(this._refreshTokenTimeout);
                    this._refreshTokenTimeout = undefined;
                }
                return;
            }
            const idTokenResult = await user.getIdTokenResult();
            const expirationTime = Date.parse(idTokenResult.expirationTime);
            this._authToken = { token: idTokenResult.token, expirationTime };
            if (this._refreshTokenTimeout !== undefined) {
                clearTimeout(this._refreshTokenTimeout);
            }
            const timeToExpiration = expirationTime - Date.now();
            this._refreshTokenTimeout = setTimeout(() => {
                this._refreshTokenTimeout = undefined;
                this.refreshToken();
            }, Math.max(0, timeToExpiration - TOKEN_EXPIRATION_BUFFER_MILLISECONDS));
        });
        if (typeof window === "undefined")
            return;
        window.addEventListener("focus", () => {
            if (!this._authToken)
                return;
            const timeToExpiration = this._authToken.expirationTime - Date.now();
            if (timeToExpiration < TOKEN_EXPIRATION_BUFFER_MILLISECONDS) {
                this.refreshToken();
            }
        });
    }
    async getToken() {
        const forceRefresh = false;
        try {
            const token = await auth.currentUser?.getIdToken(forceRefresh);
            return token;
        }
        catch (err) {
            console.error("getToken:", err);
            return undefined;
        }
    }
    async waitForToken(signal) {
        const token = await this._getTokenWithNetworkRetry(false, signal);
        if (token)
            return token;
        console.warn("waitForToken: Waiting for Firebase user");
        let resolve;
        const promise = new Promise((resolve_, _reject) => {
            resolve = resolve_;
        });
        const removeTokenListener = this.addTokenChangedListener((input) => {
            if (input === null)
                return;
            resolve(input.token);
        });
        promise.then(removeTokenListener);
        return promise;
    }
    latestToken() {
        return this._authToken?.token;
    }
    latestTokenExpirationTime() {
        return this._authToken?.expirationTime;
    }
    refreshToken = async () => {
        if (!this._activeRefreshTokenCall) {
            this._activeRefreshTokenCall = this._getTokenWithNetworkRetry(true);
        }
        await this._activeRefreshTokenCall;
        this._activeRefreshTokenCall = undefined;
    };
    addTokenChangedListener(listener) {
        return auth.onIdTokenChanged(async (user) => {
            if (!user) {
                listener(null);
                return;
            }
            const token = await user.getIdToken();
            listener({ user, token });
        });
    }
    async _getTokenWithNetworkRetry(forceRefresh = false, signal) {
        let token = undefined;
        while (auth.currentUser && token === undefined) {
            if (signal?.aborted) {
                throw signal.reason instanceof Error
                    ? signal.reason
                    : new Error(`aborted: ${signal.reason}`);
            }
            try {
                token = await auth.currentUser.getIdToken(forceRefresh);
            }
            catch (err) {
                if (err.code !== "auth/network-request-failed")
                    throw err;
                console.error("getTokenWithNetworkRetry: network request failed");
                await new Promise((resolve) => setTimeout(resolve, NETWORK_RETRY_INTERVAL_MILLISECONDS));
            }
        }
        return token;
    }
}
export default AuthManager.instance;
//# sourceMappingURL=auth.js.map